import { ADJUSTMENT_TYPE_INITIAL_VALUES } from "@/pages/configuration/models/CatalogAdjustmentType";
import { UpsertDeleteModal } from "@/pages/configuration/pages/adjustment-types/components/UpsertDeleteModal";
import { ErrorModal } from "@dexteel/mesf-core";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Autocomplete,
	Box,
	CircularProgress,
	createFilterOptions,
	IconButton,
	TextField,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Link } from "react-router-dom";
import { AdjustmentType } from "../models/AdjustmentType";
import { getAdjustmentTypes } from "../repositories/AdjustmentTypeRepository";

type Props = {
	label: string;
	value: number | null;
	onChange?: (
		id: number | null,
		requiredValue: boolean,
		requiredReason: boolean,
		unit: string | null,
	) => void;
	fieldError?: FieldError | undefined;
	disabled?: boolean;
	required?: boolean;
	isFiltering?: boolean;
};

const useAdjustmentTypes = (onError?: (error: Error) => void) => {
	return useQuery<AdjustmentType[], Error>({
		queryKey: ["adjustmentTypes"],
		queryFn: ({ signal }) => getAdjustmentTypes(signal),
		onError: onError,
	});
};

export const AdjustmentTypeSelector = ({
	label,
	value,
	onChange = () => {},
	fieldError,
	disabled = false,
	required = false,
	isFiltering = false,
}: Props) => {
	const [filterText, setFilterText] = useState("");
	const [error, setError] = useState("");
	const [showModal, setShowModal] = useState<"create" | "">("");

	const {
		data: adjustmentTypes,
		isLoading,
		refetch,
	} = useAdjustmentTypes((error) => setError(error.message));

	const orderedTypes = (adjustmentTypes ?? []).sort((a, b) => {
		if (a.AdjustmentTypeName === "Other") {
			return 1;
		}
		if (b.AdjustmentTypeName === "Other") {
			return -1;
		}
		return 0;
	});

	const selectedAdjustmentType =
		adjustmentTypes?.find((type) => type.AdjustmentTypeId === value) || null;

	const filter = createFilterOptions<AdjustmentType>();

	return (
		<>
			<Autocomplete
				id="clear-on-escape"
				clearOnEscape
				options={
					orderedTypes.filter(
						(type) => !isFiltering || type.AdjustmentTypeName !== "Other",
					) || []
				}
				getOptionLabel={(option) => option.AdjustmentTypeName}
				disabled={disabled}
				filterOptions={(options, state) => {
					const filtered = filter(options, state);

					const otherOption = orderedTypes.find(
						(option) => option.AdjustmentTypeName === "Other",
					);

					if (
						otherOption &&
						!filtered.some((option) => option.AdjustmentTypeName === "Other") &&
						!isFiltering
					) {
						filtered.push(otherOption);
					}

					return filtered as AdjustmentType[];
				}}
				renderOption={(props, option, state) => (
					<Box
						{...props}
						component="li"
						sx={{
							alignItems: "flex-start",
							display: "flex",
							flexDirection: "column",
						}}
					>
						{option.AdjustmentTypeName}
						<Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{option.RequiredReason && (
								<Box sx={{ fontSize: "0.8em", color: "grey" }}>
									Required reason |
								</Box>
							)}
							{option.RequiredValue && (
								<Box sx={{ fontSize: "0.8em", color: "grey" }}>
									Required value |
								</Box>
							)}
							{option.Unit && (
								<Box sx={{ fontSize: "0.8em", color: "grey" }}>
									Unit: '{option.Unit}'
								</Box>
							)}
						</Box>
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message}
						onChange={(event) => {
							setFilterText(event.target.value);
						}}
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										<>
											{params.InputProps.endAdornment}
											{value ? (
												<IconButton
													component={Link}
													to={`/configuration/adjustments-types/${value}`}
													target="_blank"
													size="small"
													title="See in adjustment types"
												>
													<VisibilityIcon fontSize="small" />
												</IconButton>
											) : null}
										</>
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedAdjustmentType}
				onChange={(event, newValue) => {
					if (newValue?.AdjustmentTypeName == "Other") {
						setShowModal("create");
					} else {
						onChange(
							newValue?.AdjustmentTypeId ?? null,
							newValue?.RequiredValue ?? false,
							newValue?.RequiredReason ?? false,
							newValue?.Unit ?? null,
						);
					}
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading adjustment types"
			/>
			<UpsertDeleteModal
				adjustmentType={{
					...ADJUSTMENT_TYPE_INITIAL_VALUES,
					AdjustmentTypeName: filterText,
				}}
				modal={showModal}
				onHide={async (id?: number) => {
					setShowModal("");
					const { data: updatedAdjustmentTypes } = await refetch();
					if (updatedAdjustmentTypes) {
						const type = (updatedAdjustmentTypes ?? []).find(
							(t) => t.AdjustmentTypeId == id,
						);
						onChange(
							id ?? null,
							type?.RequiredValue ?? false,
							type?.RequiredReason ?? false,
							type?.Unit ?? null,
						);
					}
				}}
			/>
		</>
	);
};
